import { IkeAngle } from '../../../unit';
import type { FormModelField } from '../../form/form.field';
import { ModelMutator } from '../collection.field';
import type { CollectionModelFieldOrientation } from './orientation.field';

export const OrientationFieldProvider = {
	MANUAL: 'manual',
	BEARING: 'bearing',
	BACK_BEARING: 'backbearing',
} as const;

/** Methods to modify an orientation field */
export class ModelMutatorOrientation extends ModelMutator<FormModelField, CollectionModelFieldOrientation> {
	/** Set the field provided by manual entry (value in degrees, [0, 360)) */
	manual(newValue: number | string | null) {
		if (newValue == null) {
			this.state.setFieldValue(this.field.id, null);
			return;
		}
		let value: number;
		if (typeof newValue === 'string') {
			const stripped = newValue.replace(/^(-?)0+(?=\d)/, '$1');
			value = parseFloat(stripped);
		} else {
			value = newValue;
		}

		if (value == null || isNaN(value)) {
			this.state.setFieldValue(this.field.id, null);
			return;
		}

		this.state.setFieldValue(this.field.id, {
			provider: OrientationFieldProvider.MANUAL,
			value: IkeAngle.normalizeDegrees(value),
		});
	}

	/** Set the value provided by bearing */
	bearing(captureId: string) {
		this.state.setFieldValue(this.field.id, {
			provider: OrientationFieldProvider.BEARING,
			captureId,
		});
	}

	/** Set the field value provided by backbearing */
	backBearing(captureId: string) {
		this.state.setFieldValue(this.field.id, {
			provider: OrientationFieldProvider.BACK_BEARING,
			captureId,
		});
	}

	/** Clone another field */
	override copy(from: CollectionModelFieldOrientation): boolean {
		if (from.value == null) {
			this.manual(null);
			return true;
		}

		if (from.isManual()) {
			this.manual(from.value);
			return true;
		}

		if (from.isBearing()) {
			this.bearing(from.stateValue.captureId);
			return true;
		}

		if (from.isBackBearing()) {
			this.backBearing(from.stateValue.captureId);
			return true;
		}

		return false;
	}

	reverse() {
		if (!this.field.hasValue()) {
			return;
		}

		if (this.field.isBearing()) {
			// TODO: IKE Field does not support 'bearing' provider so bearings are not flipped
			// Copied over from number field implementation
			return;
		}

		if (this.field.isBackBearing()) {
			// TODO: IKE Field does not support 'bearing' provider so bearings are not flipped
			// Copied over from number field implementation
			return;
		}

		// Manual orientations are not flipped
	}
}
